'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateRange } from 'react-day-picker'
import { endOfDay, endOfMonth, format, startOfDay, startOfMonth, subDays } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  AlertTriangle,
  BarChart3,
  Calendar as CalendarIcon,
  FileJson,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Settings,
  ShoppingBag,
  Trash2,
} from 'lucide-react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import { useSalesReport } from '@/hooks/useSalesReport'
import { useToast } from '@/hooks/useToast'
import { CommissionService, type CommissionSetting } from '@/services/commission.service'
import { FunctionsService } from '@/services/functions.service'
import { cn, formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
// Table components replaced with native HTML tables for scroll control
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Header } from "../layout/Header";

type SellerRow = {
  key: string
  name: string
  netSales: number
  orders: number
  /** Ganancia neta del vendedor (ventas netas − costo), base de la comisión. */
  commissionBase: number
  commission: number
}

/** Producto vendido cuyo costo no está registrado (costo = 0). */
type MissingCostProduct = {
  productId: string
  name: string
  unitsSold: number
}

type ProductRow = {
  key: string
  name: string
  units: number
  totalSales: number
  imageUrl?: string
  productIds: string[]
}

type DisplayProductRow = ProductRow & {
  resolvedImageUrl: string | null
}

const DEFAULT_RANGE: DateRange = {
  from: startOfDay(subDays(new Date(), 6)),
  to: endOfDay(new Date()),
}

const COMMISSION_DEFAULTS: Record<string, CommissionSetting> = {
  sebastian: {
    displayName: 'Sebastian',
    enabled: true,
    percentage: 5,
  },
}

const normalizeKey = (value: string) => value.trim().toLowerCase()

const parseMoney = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/\s/g, '').replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

/** Mapa de costos por producto, indexado por ID y por nombre normalizado. */
type CostLookup = {
  byId: Map<string, number>
  byName: Map<string, number>
}

/** Devuelve las líneas de una orden, tolerando distintas formas del backend. */
const getOrderLineItems = (order: any): any[] => {
  const items = order?.products ?? order?.line_items ?? order?.lineItems ?? []
  return Array.isArray(items) ? items : []
}

/** Costo unitario de una línea según el catálogo (0 si no está registrado). */
const getLineItemUnitCost = (item: any, costs: CostLookup): number => {
  const idCandidate =
    item?.id ?? item?.product_id ?? item?.productId ?? item?.productID ?? item?.variant_id
  if (idCandidate !== null && idCandidate !== undefined) {
    const byId = costs.byId.get(String(idCandidate))
    if (byId !== undefined) return byId
  }

  const name = item?.name ?? item?.productName ?? item?.title
  if (typeof name === 'string' && name.trim().length > 0) {
    const byName = costs.byName.get(normalizeKey(name))
    if (byName !== undefined) return byName
  }

  return 0
}

const getLineItemQuantity = (item: any): number => {
  const raw = Number(item?.quantity ?? item?.units ?? 0)
  return Number.isFinite(raw) && raw > 0 ? raw : 0
}

const getOrderNetSales = (order: any): number => {
  const candidates = [
    order?.netSales,
    order?.totalNetSales,
    order?.subtotal,
    order?.totalPrice,
    order?.total_price,
  ]

  for (const candidate of candidates) {
    const parsed = parseMoney(candidate)
    if (parsed > 0) {
      return parsed
    }
  }

  return 0
}

const getOrderChannel = (order: any): 'web' | 'pos' | null => {
  const sellerId = order?.sellerId ?? order?.seller_id
  if (sellerId !== null && sellerId !== undefined && `${sellerId}`.trim() !== '') {
    return 'pos'
  }

  const candidates = [
    order?.source_name,
    order?.sourceName,
    order?.source,
    order?.channel,
    order?.salesChannel,
    order?.sales_channel,
    order?.sales_channel_name,
    order?.origin,
    order?.sellerName,
    order?.seller_name,
  ]

  const rawSource =
    candidates
      .map((value) => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
      .find((value) => value.length > 0) ?? ''

  if (!rawSource) return null

  if (
    rawSource.includes('online') ||
    rawSource.includes('web') ||
    rawSource.includes('ecommerce') ||
    rawSource.includes('venta online') ||
    rawSource.includes('online store')
  ) {
    return 'web'
  }

  if (
    rawSource.includes('pos') ||
    rawSource.includes('store') ||
    rawSource.includes('tienda') ||
    rawSource.includes('floor') ||
    rawSource.includes('venta') ||
    rawSource.includes('vendedor') ||
    rawSource.includes('local') ||
    rawSource.includes('sebastian')
  ) {
    return 'pos'
  }

  return null
}

const normalizeCommissionSettingsMap = (
  settings: Record<string, CommissionSetting>
): Record<string, CommissionSetting> => {
  const normalized: Record<string, CommissionSetting> = {}

  Object.entries(settings ?? {}).forEach(([key, value]) => {
    const displayName = value?.displayName ?? key
    const normalizedKey = normalizeKey(displayName)
    normalized[normalizedKey] = {
      displayName,
      enabled: Boolean(value?.enabled),
      percentage: Number.isFinite(Number(value?.percentage))
        ? Number(value?.percentage)
        : 0,
    }
  })

  return normalized
}

const SUMMARY_CHART_CONFIG: ChartConfig = {
  netSales: { label: 'Ventas netas', color: 'hsl(142 70% 45%)' },
  orders: { label: 'Órdenes', color: 'hsl(208 80% 65%)' },
}

const SELLER_CHART_CONFIG: ChartConfig = {
  netSales: { label: 'Ventas netas', color: 'hsl(199 82% 50%)' },
  orders: { label: 'Órdenes', color: 'hsl(27 96% 61%)' },
  commission: { label: 'Comisiones', color: 'hsl(48 94% 58%)' },
}

const PRODUCT_CHART_CONFIG: ChartConfig = {
  totalSales: { label: 'Ventas totales', color: 'hsl(262 83% 62%)' },
  units: { label: 'Unidades', color: 'hsl(13 88% 63%)' },
}

export function DashboardSummary() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { report, loading, error, getSalesReport, clearReport } = useSalesReport()

  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE)
  const [selectedPreset, setSelectedPreset] = useState<'hoy' | 'mes-parcial' | 'mes-completo' | 'custom'>(
    'mes-parcial'
  )
  const [forceShopify, setForceShopify] = useState(false)
  const [commissionConfig, setCommissionConfig] = useState<Record<string, CommissionSetting>>(
    COMMISSION_DEFAULTS
  )
  const [productPreview, setProductPreview] = useState<DisplayProductRow | null>(null)
  const [isSummaryChartOpen, setIsSummaryChartOpen] = useState(false)
  const [isSellerChartOpen, setIsSellerChartOpen] = useState(false)
  const [isProductChartOpen, setIsProductChartOpen] = useState(false)
  const [isProductDebugOpen, setIsProductDebugOpen] = useState(false)
  const [productImages, setProductImages] = useState<
    Record<
      string,
      { imageUrl?: string | null; handle?: string | null; title?: string | null; inventory?: number | null }
    >
  >({})
  const [isLogOpen, setIsLogOpen] = useState(false)
  const [productCosts, setProductCosts] = useState<CostLookup>(() => ({
    byId: new Map<string, number>(),
    byName: new Map<string, number>(),
  }))
  const [costsLoaded, setCostsLoaded] = useState(false)

  // Cargar costos de productos (para calcular la ganancia neta y, sobre ella, las comisiones)
  useEffect(() => {
    let cancelled = false

    FunctionsService.getProductsFromFirestore()
      .then((res) => {
        if (cancelled) return
        const byId = new Map<string, number>()
        const byName = new Map<string, number>()
        const products = res.data?.products ?? []

        for (const product of products) {
          const cost = parseMoney(product?.cost)
          if (product?.id !== null && product?.id !== undefined) {
            byId.set(String(product.id), cost)
          }
          if (typeof product?.title === 'string' && product.title.trim().length > 0) {
            byName.set(normalizeKey(product.title), cost)
          }
        }

        setProductCosts({ byId, byName })
        setCostsLoaded(true)
      })
      .catch((err: any) => {
        console.error('Error loading product costs for commissions', err)
        if (!cancelled) {
          setCostsLoaded(true)
          toast({
            title: 'No se pudieron cargar los costos',
            description:
              'Las comisiones podrían mostrarse en $0. Sincroniza los productos en la página de Costos.',
            variant: 'destructive',
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [toast])

  const formatRangeLabel = useCallback((range: DateRange) => {
    if (!range.from || !range.to) return 'Selecciona un rango'

    const sameDay = range.from.toDateString() === range.to.toDateString()
    return sameDay
      ? format(range.from, "EEEE d 'de' MMMM yyyy", { locale: es })
      : `${format(range.from, "d 'de' MMMM", { locale: es })} - ${format(range.to, "d 'de' MMMM yyyy", { locale: es })}`
  }, [])

  const ensureCommissionEntries = useCallback(
    (nextSellers: { sellerName: string }[]) => {
      if (!nextSellers?.length) return

      setCommissionConfig((prev) => {
        const next = { ...prev }
        let changed = false

        nextSellers.forEach(({ sellerName }) => {
          const key = normalizeKey(sellerName)
          if (!next[key]) {
            next[key] = {
              displayName: sellerName,
              enabled: Boolean(COMMISSION_DEFAULTS[key]?.enabled),
              percentage: COMMISSION_DEFAULTS[key]?.percentage ?? 0,
            }
            changed = true
          } else if (next[key].displayName !== sellerName) {
            next[key] = { ...next[key], displayName: sellerName }
            changed = true
          }
        })

        return changed ? next : prev
      })
    },
    []
  )

  const runReport = useCallback(
    async (range?: DateRange) => {
      const targetRange = range ?? dateRange
      if (!targetRange.from || !targetRange.to) {
        toast({
          title: 'Rango incompleto',
          description: 'Selecciona una fecha inicial y final para generar el reporte.',
          variant: 'destructive',
        })
        return
      }

      const result = await getSalesReport(targetRange.from, targetRange.to, forceShopify)

      if (!result) {
        toast({
          title: 'Sin datos',
          description: 'No se pudieron obtener ventas para el período seleccionado.',
          variant: 'destructive',
        })
        return
      }

      ensureCommissionEntries(result.sellers ?? [])
    },
    [dateRange, ensureCommissionEntries, forceShopify, getSalesReport, toast]
  )

  useEffect(() => {
    runReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false

    CommissionService.getSettings()
      .then((stored) => {
        if (cancelled || !stored) return
        const normalized = normalizeCommissionSettingsMap(stored)
        setCommissionConfig((prev) => ({ ...prev, ...normalized }))
      })
      .catch((err: any) => {
        console.error('Error loading commission settings', err)
        toast({
          title: 'No se pudo cargar comisiones',
          description: err?.message ?? 'Intenta de nuevo más tarde.',
          variant: 'destructive',
        })
      })

    return () => {
      cancelled = true
    }
  }, [toast])

  useEffect(() => {
    if (report?.sellers) {
      ensureCommissionEntries(report.sellers)
    }
  }, [ensureCommissionEntries, report?.sellers])

  const handlePresetChange = useCallback(
    (preset: 'hoy' | 'mes-parcial' | 'mes-completo') => {
      const now = new Date()
      let nextRange: DateRange

      if (preset === 'hoy') {
        nextRange = {
          from: startOfDay(now),
          to: endOfDay(now),
        }
      } else if (preset === 'mes-parcial') {
        nextRange = {
          from: startOfDay(startOfMonth(now)),
          to: endOfDay(now),
        }
      } else {
        const monthStart = startOfMonth(now)
        nextRange = {
          from: startOfDay(monthStart),
          to: endOfDay(endOfMonth(monthStart)),
        }
      }

      setSelectedPreset(preset)
      setDateRange(nextRange)
      runReport(nextRange)
    },
    [runReport]
  )

  const handleClearCache = useCallback(async () => {
    try {
      clearReport()
      const payload =
        dateRange.from && dateRange.to
          ? { from: dateRange.from, to: dateRange.to }
          : undefined
      const response = await FunctionsService.clearCache(payload)

      if (response.error) {
        toast({
          title: 'Error al limpiar caché',
          description: response.error,
          variant: 'destructive',
        })
        return
      }

      toast({
        title: 'Caché limpiado',
        description: 'La próxima generación forzará datos actualizados.',
      })
    } catch (err: any) {
      toast({
        title: 'Error inesperado',
        description: err.message ?? 'No se pudo limpiar la caché.',
        variant: 'destructive',
      })
    }
  }, [clearReport, dateRange.from, dateRange.to, toast])

  const totalNetSales = report?.totalNetSales ?? 0
  const totalOrders = report?.totalOrders ?? 0

  const totalTaxes = useMemo(() => {
    if (!report?.orders) return 0
    return report.orders.reduce((sum, order) => sum + parseMoney((order as any).tax), 0)
  }, [report?.orders])

  const totalShipments = useMemo(() => {
    if (!report?.orders) return 0
    return report.orders.reduce((count, order) => {
      const status = String(
        (order as any).fulfillment_status ?? (order as any).status ?? ''
      ).toLowerCase()
      if (['fulfilled', 'shipped', 'completed', 'delivered', 'paid'].includes(status)) {
        return count + 1
      }
      return count
    }, 0)
  }, [report?.orders])

  const sellerRows: SellerRow[] = useMemo(() => {
    if (!report?.sellers) return []

    return report.sellers
      .map((seller) => {
        const key = normalizeKey(seller.sellerName)
        const config = commissionConfig[key]

        // Base de la comisión = ganancia neta, calculada ORDEN POR ORDEN.
        // Cada orden aporta max(0, venta − costo): una venta en oferta por debajo del
        // costo aporta $0 y NO resta comisión de las demás órdenes del vendedor.
        // Los productos sin costo registrado aportan $0 de costo.
        let commissionBase = 0
        for (const order of (seller as any).orders ?? []) {
          let orderCost = 0
          for (const item of getOrderLineItems(order)) {
            orderCost += getLineItemUnitCost(item, productCosts) * getLineItemQuantity(item)
          }
          commissionBase += Math.max(0, getOrderNetSales(order) - orderCost)
        }

        const netSales = seller.totalNetSales ?? 0

        const commission =
          config?.enabled && config.percentage > 0
            ? commissionBase * (config.percentage / 100)
            : 0

        return {
          key,
          name: seller.sellerName,
          netSales,
          orders: seller.totalOrders ?? 0,
          commissionBase,
          commission,
        }
      })
      .sort((a, b) => b.netSales - a.netSales)
  }, [commissionConfig, report?.sellers, productCosts])

  const totalCommissions = useMemo(
    () => sellerRows.reduce((sum, seller) => sum + seller.commission, 0),
    [sellerRows]
  )

  const bestSeller = useMemo(() => sellerRows[0]?.name ?? 'Sin registros', [sellerRows])

  // Productos que se vendieron pero no tienen costo registrado (costo = 0).
  // Se listan para ajustarlos en Shopify: sin costo, la ganancia y la comisión quedan sobreestimadas.
  const productsWithoutCost = useMemo<MissingCostProduct[]>(() => {
    if (!costsLoaded || !report?.orders) return []

    const map = new Map<string, MissingCostProduct>()
    for (const order of report.orders) {
      for (const item of getOrderLineItems(order)) {
        const qty = getLineItemQuantity(item)
        if (qty <= 0) continue
        if (getLineItemUnitCost(item, productCosts) > 0) continue

        const idCandidate =
          (item as any)?.id ??
          (item as any)?.product_id ??
          (item as any)?.productId ??
          (item as any)?.variant_id
        const name =
          (item as any)?.name ?? (item as any)?.productName ?? (item as any)?.title ?? 'Producto sin nombre'
        const productId = idCandidate != null ? String(idCandidate) : `name:${normalizeKey(name)}`

        const existing = map.get(productId)
        if (existing) {
          existing.unitsSold += qty
        } else {
          map.set(productId, { productId, name, unitsSold: qty })
        }
      }
    }

    return Array.from(map.values()).sort((a, b) => b.unitsSold - a.unitsSold)
  }, [costsLoaded, report?.orders, productCosts])

  const { webSales, posSales } = useMemo(() => {
    if (!report?.orders) {
      return { webSales: 0, posSales: 0 }
    }

    return report.orders.reduce(
      (acc, order) => {
        const netSales = getOrderNetSales(order)
        const channel = getOrderChannel(order)
        if (channel === 'web') {
          acc.webSales += netSales
        } else if (channel === 'pos') {
          acc.posSales += netSales
        }
        return acc
      },
      { webSales: 0, posSales: 0 }
    )
  }, [report?.orders])

  const productRows: ProductRow[] = useMemo(() => {
    if (report?.products?.length) {
      return report.products
        .map((product, index) => {
          const record = product as any
          const productIdValue =
            record.productId ?? record.productID ?? record.id ?? record.shopifyProductId

          return {
            key: `${product.productName}-${index}`,
            name: product.productName,
            units: product.unitsSold ?? 0,
            totalSales: product.totalSales ?? 0,
            imageUrl: product.imageUrl ?? (product as any).image ?? undefined,
            productIds:
              productIdValue !== null && productIdValue !== undefined
                ? [String(productIdValue)]
                : [],
          }
        })
        .sort((a, b) => b.totalSales - a.totalSales)
    }

    if (!report?.orders) return []

    const aggregate = new Map<
      string,
      { name: string; units: number; sales: number; imageUrl?: string; productIds: Set<string> }
    >()

    report.orders.forEach((order) => {
      const items = (order as any).products ?? order.line_items ?? []
      items.forEach((item: any) => {
        const name: string = item.productName ?? item.name ?? 'Producto sin nombre'
        const key = name.toLowerCase()
        const quantityRaw = Number(item.units ?? item.quantity ?? 0)
        const quantity = Number.isFinite(quantityRaw) ? quantityRaw : 0

        const totalSalesRaw = parseMoney(item.totalSales)
        const priceRaw = parseMoney(item.price)
        const totalSales = totalSalesRaw > 0 ? totalSalesRaw : priceRaw * quantity

        const productIdCandidate =
          item.productId ?? item.product_id ?? item.id ?? item.productID ?? item.variant_id
        const productId =
          productIdCandidate !== null && productIdCandidate !== undefined
            ? String(productIdCandidate)
            : undefined

        const imageUrl: string | undefined =
          item.imageUrl ?? item.image ?? item.image_url ?? item.thumbnail ?? undefined

        const current =
          aggregate.get(key) ??
          ({
            name,
            units: 0,
            sales: 0,
            imageUrl,
            productIds: new Set<string>(),
          } as const)

        const nextProductIds =
          productId !== undefined ? new Set(current.productIds).add(productId) : current.productIds
        aggregate.set(key, {
          name,
          units: current.units + quantity,
          sales: current.sales + totalSales,
          imageUrl: current.imageUrl ?? imageUrl,
          productIds: nextProductIds,
        })
      })
    })

    return Array.from(aggregate.entries())
      .map(([key, value]) => ({
        key,
        name: value.name,
        units: value.units,
        totalSales: value.sales,
        imageUrl: value.imageUrl,
        productIds: Array.from(value.productIds),
      }))
      .sort((a, b) => b.totalSales - a.totalSales)
  }, [report?.orders, report?.products])

  useEffect(() => {
    const pending = new Set<string>()
    productRows.forEach((row) => {
      row.productIds.forEach((id) => {
        const trimmed = id?.trim()
        if (!trimmed) return
        if (!productImages[trimmed]) {
          pending.add(trimmed)
        }
      })
    })

    if (pending.size === 0) return

    let cancelled = false
    const ids = Array.from(pending)

    FunctionsService.getProductsByIds(ids)
      .then((response) => {
        if (cancelled) return
        if (response.error) {
          toast({
            title: 'No se pudieron cargar las imágenes',
            description: response.error,
            variant: 'destructive',
          })
          return
        }

        if (response.data) {
          setProductImages((prev) => {
            const next = { ...prev }
            Object.entries(response.data!).forEach(([id, info]) => {
              next[id] = info ?? {}
            })
            return next
          })
        }
      })
      .catch((error: any) => {
        if (cancelled) return
        toast({
          title: 'Error al cargar imágenes',
          description: error?.message ?? 'No se pudieron obtener los datos del producto.',
          variant: 'destructive',
        })
      })

    return () => {
      cancelled = true
    }
  }, [productRows, productImages, toast])

  const displayProductRows: DisplayProductRow[] = useMemo(() => {
    return productRows.map((row) => {
      const fallbackImage =
        row.productIds
          .map((id) => productImages[id]?.imageUrl)
          .find((value) => typeof value === 'string' && value.length > 0) ?? null

      return {
        ...row,
        resolvedImageUrl: row.imageUrl ?? fallbackImage,
      }
    })
  }, [productRows, productImages])

  const productDebugData = useMemo(
    () =>
      displayProductRows.map((product): {
        name: string
        totalSales: number
        units: number
        imageUrl: string | null
        resolvedImageUrl: string | null
        productIds: string[]
        lookups: Record<string, { imageUrl?: string | null; handle?: string | null; title?: string | null }>
      } => ({
        name: product.name,
        totalSales: product.totalSales,
        units: product.units,
        imageUrl: product.imageUrl ?? null,
        resolvedImageUrl: product.resolvedImageUrl,
        productIds: product.productIds,
        lookups: product.productIds.reduce<Record<string, { imageUrl?: string | null; handle?: string | null; title?: string | null }>>(
          (acc, id: string) => {
            const info = productImages[id]
            if (info) {
              acc[id] = info
            }
            return acc
          },
          {}
        ),
      })),
    [displayProductRows, productImages]
  )

  const summaryChartData = useMemo(() => {
    if (!report?.orders) return []

    const buckets = new Map<string, { label: string; netSales: number; orders: number }>()

    report.orders.forEach((order: any) => {
      const dateValue = order?.date ?? order?.created_at ?? order?.createdAt
      if (!dateValue) return

      const parsedDate = new Date(dateValue)
      if (Number.isNaN(parsedDate.getTime())) return

      const key = format(parsedDate, 'yyyy-MM-dd')
      const bucket =
        buckets.get(key) ??
        ({
          label: format(parsedDate, 'dd MMM', { locale: es }),
          netSales: 0,
          orders: 0,
        } as const)

      buckets.set(key, {
        label: bucket.label,
        netSales: bucket.netSales + getOrderNetSales(order),
        orders: bucket.orders + 1,
      })
    })

    return Array.from(buckets.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, value]) => value)
  }, [report?.orders])

  const sellerChartData = useMemo(
    () =>
      sellerRows.map((seller) => ({
        name: seller.name,
        netSales: seller.netSales,
        orders: seller.orders,
        commission: seller.commission,
      })),
    [sellerRows]
  )

  const productChartData = useMemo(
    () =>
      productRows.slice(0, 12).map((product) => ({
        name: product.name,
        totalSales: product.totalSales,
        units: product.units,
      })),
    [productRows]
  )

  const handleCommissionSave = useCallback(
    async (next: Record<string, CommissionSetting>) => {
      const normalized = normalizeCommissionSettingsMap(next)

      try {
        await CommissionService.saveSettings(normalized)
        setCommissionConfig(normalized)
        toast({
          title: 'Comisiones guardadas',
          description: 'Las asignaciones de comisiones se aplicaron al resumen.',
        })
      } catch (error: any) {
        toast({
          title: 'Error al guardar comisiones',
          description: error?.message ?? 'No se pudo actualizar la configuración.',
          variant: 'destructive',
        })
        throw error
      }
    },
    [toast]
  )

  return (
    <section className="space-y-8">
      <Header />
      <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
        <CardContent className="pt-4 space-y-3">
          {/* Row 1: Presets + calendar */}
          <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                selectedPreset === 'hoy' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
              )}
              onClick={() => handlePresetChange('hoy')}
            >
              Hoy
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                selectedPreset === 'mes-parcial' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
              )}
              onClick={() => handlePresetChange('mes-parcial')}
            >
              Días del mes
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                selectedPreset === 'mes-completo' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
              )}
              onClick={() => handlePresetChange('mes-completo')}
            >
              Mes completo
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 flex items-center gap-2 bg-white/10 text-white hover:bg-white/20"
                >
                  <CalendarIcon className="h-4 w-4" />
                  <span>{formatRangeLabel(dateRange)}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto bg-gray-950/95 p-0 text-white" align="start">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={dateRange}
                  defaultMonth={dateRange.from}
                  onSelect={(value) => {
                    if (!value) return
                    setSelectedPreset('custom')
                    setDateRange({
                      from: value.from ? startOfDay(value.from) : undefined,
                      to: value.to ? endOfDay(value.to) : undefined,
                    })
                  }}
                />
                <div className="flex justify-end border-t border-white/10 p-3">
                  <Button
                    size="sm"
                    className="bg-emerald-500 text-white hover:bg-emerald-600"
                    onClick={() => runReport()}
                  >
                    Generar
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Row 2: Actions inline */}
          <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
            <Button
              size="sm"
              className="flex-shrink-0 flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-500/90"
              onClick={() => runReport()}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Generar
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-red-500/20 text-red-200 hover:bg-red-500/30"
              onClick={handleClearCache}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4" />
              Limpiar caché
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-shrink-0 gap-2 bg-white/10 text-white hover:bg-white/20"
              onClick={() => setIsSummaryChartOpen(true)}
            >
              <BarChart3 className="h-4 w-4" />
              Ver gráficas
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-shrink-0 gap-2 bg-white/10 text-white hover:bg-white/20"
              onClick={() => setIsLogOpen(true)}
            >
              <FileJson className="h-4 w-4" />
              Logs
            </Button>
            <div className="flex-shrink-0 flex items-center gap-2 rounded-md bg-white/5 px-3 py-2 text-sm text-white">
              <Switch
                id="force-shopify-summary"
                checked={forceShopify}
                onCheckedChange={setForceShopify}
                className="data-[state=checked]:bg-blue-500"
              />
              <Label htmlFor="force-shopify-summary" className="cursor-pointer text-sm text-white">
                Forzar datos de Shopify {forceShopify && '(habilitado)'}
              </Label>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard title="Ventas Netas Totales" value={formatCurrency(totalNetSales)} />
        <MetricCard title="Órdenes Totales" value={totalOrders.toLocaleString()} />
        <MetricCard title="Impuestos Totales" value={formatCurrency(totalTaxes)} />
        <MetricCard title="Envíos Totales" value={totalShipments.toLocaleString()} />
        <MetricCard title="Comisiones Totales" value={formatCurrency(totalCommissions)} />
        <MetricCard title="Mejor Vendedor" value={bestSeller} />
        <MetricCard title="Ventas Web" value={formatCurrency(webSales)} />
        <MetricCard title="Ventas POS" value={formatCurrency(posSales)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-white/10 bg-white/5 text-white backdrop-blur overflow-hidden">
          <CardHeader className="py-4 px-5 space-y-2">
            <div>
              <CardTitle className="text-sm font-semibold text-white">Desglose por Vendedor</CardTitle>
              <CardDescription className="text-xs text-white/50 mt-0.5">Comisión = ganancia neta × %</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 bg-white/10 text-white hover:bg-white/20 border-white/10 text-xs h-7 px-2"
                onClick={() => setIsSellerChartOpen(true)}
              >
                <BarChart3 className="h-3.5 w-3.5" />
                Gráfica
              </Button>
              <CommissionConfigDialog
                sellers={sellerRows.map((seller) => ({
                  key: seller.key,
                  name: seller.name,
                }))}
                commissionConfig={commissionConfig}
                onSave={handleCommissionSave}
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-white/80" />
              </div>
            )}

            {!loading && sellerRows.length === 0 && (
              <p className="py-6 text-center text-sm text-white/40">
                No hay vendedores para el período seleccionado.
              </p>
            )}

            {!loading && sellerRows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Vendedor</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Ventas</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Órdenes</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Comisión</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {sellerRows.map((seller) => (
                      <tr key={seller.key} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-2.5 font-medium text-white whitespace-nowrap">{seller.name}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-400 font-medium whitespace-nowrap">{formatCurrency(seller.netSales)}</td>
                        <td className="px-4 py-2.5 text-right text-white/70 whitespace-nowrap">{seller.orders.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <div className="font-medium text-white/90">{formatCurrency(seller.commission)}</div>
                          <div className="text-[10px] text-white/40">ganancia: {formatCurrency(seller.commissionBase)}</div>
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <button
                            onClick={() => navigate('/stats')}
                            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            Ver →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/5 text-white backdrop-blur overflow-hidden">
          <CardHeader className="py-4 px-5 space-y-2">
            <div>
              <CardTitle className="text-sm font-semibold text-white">Ventas de Productos</CardTitle>
              <CardDescription className="text-xs text-white/50 mt-0.5">Unidades vendidas por producto</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 bg-white/10 text-white hover:bg-white/20 border-white/10 text-xs h-7 px-2"
                onClick={() => setIsProductChartOpen(true)}
              >
                <ShoppingBag className="h-3.5 w-3.5" />
                Gráfica
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-white/80" />
              </div>
            )}

            {!loading && displayProductRows.length === 0 && (
              <p className="py-6 text-center text-sm text-white/40">
                No hay ventas de productos para el período seleccionado.
              </p>
            )}

            {!loading && displayProductRows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide">Producto</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Uds</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Ventas</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {displayProductRows.map((product) => (
                      <tr key={product.key} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-2.5 font-medium text-white">
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-md bg-black/40">
                              <img
                                src={product.resolvedImageUrl || '/placeholder.svg'}
                                alt={product.name}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </div>
                            <span className="truncate max-w-[160px] text-sm">{product.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right text-white/70 whitespace-nowrap">{product.units.toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-400 font-medium whitespace-nowrap">{formatCurrency(product.totalSales)}</td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <button
                            onClick={() => setProductPreview(product)}
                            className="text-white/40 hover:text-white transition-colors"
                            aria-label="Ver foto"
                          >
                            <ImageIcon className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {!loading && costsLoaded && productsWithoutCost.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5 text-white backdrop-blur overflow-hidden">
          <CardHeader className="py-4 px-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-500/20">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-sm font-semibold text-amber-100">
                  Productos vendidos sin costo ({productsWithoutCost.length})
                </CardTitle>
                <CardDescription className="text-xs text-amber-200/60 mt-0.5">
                  Estos productos se vendieron pero no tienen costo registrado, así que su ganancia (y la comisión) se sobreestima.
                  Ajústalos en Shopify y sincroniza en la página de Costos.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide">Producto</th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">ID Shopify</th>
                    <th className="px-5 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Uds vendidas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {productsWithoutCost.map((product) => (
                    <tr key={product.productId} className="hover:bg-white/5 transition-colors">
                      <td className="px-5 py-2.5 font-medium text-white">
                        <span className="block max-w-[280px] truncate">{product.name}</span>
                      </td>
                      <td className="px-5 py-2.5 text-white/40 tabular-nums whitespace-nowrap">
                        {product.productId.startsWith('name:') ? '—' : product.productId}
                      </td>
                      <td className="px-5 py-2.5 text-right text-amber-300 font-medium tabular-nums whitespace-nowrap">
                        {product.unitsSold.toLocaleString('es-ES')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isSummaryChartOpen} onOpenChange={setIsSummaryChartOpen}>
        <DialogContent className="max-w-5xl border-white/10 bg-gray-950/95 text-white">
          <DialogHeader>
            <DialogTitle>Gráficas del período</DialogTitle>
            <DialogDescription className="text-white/70">
              Evolución de ventas netas y órdenes por día en el rango seleccionado.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 h-[340px]">
            {summaryChartData.length > 0 ? (
              <ChartContainer config={SUMMARY_CHART_CONFIG} className="h-full">
                <ResponsiveContainer>
                  <LineChart data={summaryChartData}>
                    <CartesianGrid stroke="hsl(var(--muted) / 0.3)" strokeDasharray="3 3" />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" />
                    <YAxis
                      yAxisId="left"
                      stroke="hsl(var(--muted-foreground))"
                      tickFormatter={(value) => formatCurrency(Number(value))}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="hsl(var(--muted-foreground))"
                      allowDecimals={false}
                      tickFormatter={(value) => Number(value).toLocaleString('es-ES')}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          className="border-white/10 bg-gray-900/90 text-white"
                          formatter={(value, name) =>
                            name === 'Órdenes'
                              ? Number(value ?? 0).toLocaleString('es-ES')
                              : formatCurrency(Number(value ?? 0))
                          }
                        />
                      }
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="netSales"
                      name="Ventas netas"
                      stroke="var(--color-netSales)"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orders"
                      name="Órdenes"
                      stroke="var(--color-orders)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="flex h-full items-center justify-center rounded-md border border-dashed border-white/20 text-sm text-white/60">
                No hay datos suficientes para mostrar gráficas en este período.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isSellerChartOpen} onOpenChange={setIsSellerChartOpen}>
        <DialogContent className="max-w-4xl border-white/10 bg-gray-950/95 text-white">
          <DialogHeader>
            <DialogTitle>Gráfica por vendedor</DialogTitle>
            <DialogDescription className="text-white/70">
              Comparativo de ventas netas, comisiones y órdenes por vendedor.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 h-[340px]">
            {sellerChartData.length > 0 ? (
              <ChartContainer config={SELLER_CHART_CONFIG} className="h-full">
                <ResponsiveContainer>
                  <ComposedChart data={sellerChartData}>
                    <CartesianGrid stroke="hsl(var(--muted) / 0.3)" strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                    <YAxis
                      yAxisId="left"
                      stroke="hsl(var(--muted-foreground))"
                      tickFormatter={(value) => formatCurrency(Number(value))}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="hsl(var(--muted-foreground))"
                      allowDecimals={false}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          className="border-white/10 bg-gray-900/90 text-white"
                          formatter={(value, name) => {
                            if (name === 'Órdenes') {
                              return Number(value ?? 0).toLocaleString('es-ES')
                            }
                            return formatCurrency(Number(value ?? 0))
                          }}
                        />
                      }
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="netSales"
                      name="Ventas netas"
                      fill="var(--color-netSales)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="commission"
                      name="Comisiones"
                      fill="var(--color-commission)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orders"
                      name="Órdenes"
                      stroke="var(--color-orders)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="flex h-full items-center justify-center rounded-md border border-dashed border-white/20 text-sm text-white/60">
                No hay vendedores con datos suficientes en el rango actual.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isProductChartOpen} onOpenChange={setIsProductChartOpen}>
        <DialogContent className="max-w-4xl border-white/10 bg-gray-950/95 text-white">
          <DialogHeader>
            <DialogTitle>Gráfica de productos</DialogTitle>
            <DialogDescription className="text-white/70">
              Top de productos por ventas totales y unidades vendidas.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 h-[340px]">
            {productChartData.length > 0 ? (
              <ChartContainer config={PRODUCT_CHART_CONFIG} className="h-full">
                <ResponsiveContainer>
                  <ComposedChart data={productChartData}>
                    <CartesianGrid stroke="hsl(var(--muted) / 0.3)" strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                    <YAxis
                      yAxisId="left"
                      stroke="hsl(var(--muted-foreground))"
                      tickFormatter={(value) => formatCurrency(Number(value))}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="hsl(var(--muted-foreground))"
                      allowDecimals={false}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          className="border-white/10 bg-gray-900/90 text-white"
                          formatter={(value, name) =>
                            name === 'Unidades'
                              ? Number(value ?? 0).toLocaleString('es-ES')
                              : formatCurrency(Number(value ?? 0))
                          }
                        />
                      }
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="totalSales"
                      name="Ventas totales"
                      fill="var(--color-totalSales)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="units"
                      name="Unidades"
                      stroke="var(--color-units)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="flex h-full items-center justify-center rounded-md border border-dashed border-white/20 text-sm text-white/60">
                No hay productos con ventas registradas en este período.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isProductDebugOpen} onOpenChange={setIsProductDebugOpen}>
        <DialogContent className="max-w-3xl border-white/10 bg-gray-950/95 text-white">
          <DialogHeader>
            <DialogTitle>Debug de imágenes de productos</DialogTitle>
            <DialogDescription className="text-white/70">
              Usa esta información para verificar los IDs de producto enviados por Shopify y
              diagnosticar por qué no se carga la imagen.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-white/10 bg-black/40 p-4">
            {productDebugData.length > 0 ? (
              <pre className="max-h-[380px] overflow-y-auto text-xs text-emerald-200">
                {JSON.stringify(productDebugData, null, 2)}
              </pre>
            ) : (
              <p className="py-4 text-sm text-white/60">
                No hay productos disponibles en el rango seleccionado.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(productPreview)} onOpenChange={(open) => !open && setProductPreview(null)}>
        <DialogContent className="max-w-lg border-white/10 bg-gray-950/95 text-white">
          {productPreview && (
            <>
              <DialogHeader>
                <DialogTitle>{productPreview.name}</DialogTitle>
                <DialogDescription className="text-white/70">
                  Unidades vendidas: {productPreview.units.toLocaleString()} | Ventas:{' '}
                  {formatCurrency(productPreview.totalSales)}
                </DialogDescription>
                <p className="text-xs text-white/60">
                  IDs de producto:{' '}
                  {productPreview.productIds.length > 0
                    ? productPreview.productIds.join(', ')
                    : 'No disponible'}
                </p>
              </DialogHeader>
              <div className="overflow-hidden rounded-lg border border-white/10 bg-black/40">
                <img
                  src={productPreview.resolvedImageUrl || '/placeholder.svg'}
                  alt={productPreview.name}
                  className="h-auto w-full object-contain bg-black/60"
                />
                {!productPreview.resolvedImageUrl && (
                  <p className="px-4 py-3 text-center text-sm text-white/60">
                    No hay imagen disponible para este producto.
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isLogOpen} onOpenChange={setIsLogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Logs de Depuración</DialogTitle>
            <DialogDescription>
              Información técnica del reporte actual y errores
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
             <div>
              <h3 className="font-semibold mb-2">Estado</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                 <div>Loading: {loading ? 'Sí' : 'No'}</div>
                 <div>Error: {error ?? 'Ninguno'}</div>
                 <div>Rango: {formatRangeLabel(dateRange)}</div>
              </div>
            </div>
            
            <div>
               <h3 className="font-semibold mb-2">Datos Crudos (JSON)</h3>
               <div className="bg-slate-950 text-slate-50 p-4 rounded-md overflow-x-auto">
                 <pre className="text-xs font-mono">
                   {JSON.stringify({
                      dateRange,
                      error,
                      report,
                      commissionConfig
                   }, null, 2)}
                 </pre>
               </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}

type MetricCardProps = {
  title: string
  value: string
}

function MetricCard({ title, value }: MetricCardProps) {
  return (
    <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
      <CardHeader className="space-y-1">
        <CardDescription className="text-white/60">{title}</CardDescription>
        <CardTitle className="text-2xl font-semibold text-white">{value}</CardTitle>
      </CardHeader>
    </Card>
  )
}

type CommissionConfigDialogProps = {
  sellers: { key: string; name: string }[]
  commissionConfig: Record<string, CommissionSetting>
  onSave: (next: Record<string, CommissionSetting>) => Promise<void>
}

function CommissionConfigDialog({ sellers, commissionConfig, onSave }: CommissionConfigDialogProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Record<string, CommissionSetting>>(commissionConfig)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      const nextDraft: Record<string, CommissionSetting> = {}

      sellers.forEach((seller) => {
        const current = commissionConfig[seller.key] ?? {
          displayName: seller.name,
          enabled: false,
          percentage: 0,
        }

        nextDraft[seller.key] = { ...current, displayName: seller.name }
      })

      setDraft(nextDraft)
    }
  }, [commissionConfig, open, sellers])

  const handleToggle = (key: string, value: boolean) => {
    setDraft((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? { displayName: key, enabled: false, percentage: 0 }),
        enabled: value,
      },
    }))
  }

  const handlePercentageChange = (key: string, value: string) => {
    const parsed = Number(value)
    setDraft((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? { displayName: key, enabled: false, percentage: 0 }),
        percentage: Number.isFinite(parsed) ? parsed : 0,
      },
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(draft)
      setOpen(false)
    } catch (error) {
      console.error('Error saving commission configuration', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 bg-white/10 text-white hover:bg-white/20"
        >
          <Settings className="h-4 w-4" />
          Configurar comisiones
        </Button>
      </DialogTrigger>
      <DialogContent className="flex flex-col max-h-[85vh] max-w-lg border-white/10 bg-gray-950/95 text-white overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Configurar comisiones</DialogTitle>
          <DialogDescription className="text-white/70">
            Define qué vendedores reciben comisión y el porcentaje sobre sus ventas netas.
          </DialogDescription>
        </DialogHeader>

        {sellers.length === 0 ? (
          <p className="rounded-md border border-white/10 bg-black/30 py-4 text-center text-sm text-white/60">
            Aún no hay vendedores para configurar.
          </p>
        ) : (
          <div className="flex-1 overflow-y-auto min-h-0 pr-1 space-y-3 py-1">
            {sellers.map((seller) => {
              const setting = draft[seller.key]

              return (
                <div
                  key={seller.key}
                  className="flex items-center justify-between rounded-md border border-white/10 bg-black/30 px-4 py-3 gap-3"
                >
                  <p className="text-sm font-medium text-white flex-1 min-w-0 truncate">{seller.name}</p>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex flex-col items-end gap-1">
                      <Label htmlFor={`commission-${seller.key}`} className="text-xs text-white/50">
                        % comisión
                      </Label>
                      <Input
                        id={`commission-${seller.key}`}
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        value={setting?.percentage ?? 0}
                        onChange={(event) => handlePercentageChange(seller.key, event.target.value)}
                        disabled={!setting?.enabled}
                        className="h-8 w-20 bg-white/10 text-right text-white text-sm"
                      />
                    </div>
                    <Switch
                      checked={Boolean(setting?.enabled)}
                      onCheckedChange={(checked) => handleToggle(seller.key, checked)}
                      className="data-[state=checked]:bg-emerald-500"
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <DialogFooter className="flex-shrink-0 pt-4 border-t border-white/10 mt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="ml-auto flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-500/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}















